/**
 * Supabase Auto-Logger
 *
 * Monkey-patches the Supabase client's `.from()` method so that every
 * insert / update / upsert / delete call made by a salesman is
 * automatically written to the `activity_logs` table.
 *
 * This guarantees 100% coverage — no manual `logActivity()` calls needed.
 *
 * Usage:
 *   1. Call `initSupabaseLogger()` once at app startup (e.g. in auth context).
 *   2. Call `setLoggerUser(email, role)` whenever the authenticated user changes.
 *   3. That's it — every Supabase mutation is now logged automatically.
 */

import { supabase } from './client';

// ──────────────────────────────────────────────
// Current user state (set from auth context)
// ──────────────────────────────────────────────
let _currentUserEmail: string | null = null;
let _currentUserRole: string | null = null;

export function setLoggerUser(email: string | null | undefined, role: string | null | undefined) {
  _currentUserEmail = email || null;
  _currentUserRole = role || null;
}

// ──────────────────────────────────────────────
// Tables we should never log (to avoid infinite
// loops and noisy internal bookkeeping)
// ──────────────────────────────────────────────
const IGNORED_TABLES = new Set([
  'activity_logs',       // prevent infinite recursion
  'viewed_emails',       // high-frequency read-tracking
  'snoozed_emails',      // minor UI state
  'active_convos',       // transient state
  'notifications',       // system-generated
  'app_config',          // admin config
]);

// Human-readable table → resource type mapping
const TABLE_RESOURCE_MAP: Record<string, string> = {
  leads: 'lead',
  quotes: 'quote',
  invoices: 'invoice',
  agreements: 'agreement',
  job_reports: 'job_report',
  calendar_events: 'calendar_event',
  email_drafts: 'email_draft',
  email_follow_ups: 'email_follow_up',
  contractors: 'contractor',
  inventory: 'inventory',
  tasks: 'task',
  teams: 'team',
  support_messages: 'support_message',
  company_settings: 'company_settings',
  company_contracts: 'company_contract',
  social_posts: 'social_post',
  social_accounts: 'social_account',
  campaign_drafts: 'campaign_draft',
  installations: 'installation',
  notes: 'note',
  workflow_descriptions: 'workflow',
  marketing_tasks: 'marketing_task',
  ad_split_tests: 'ad_split_test',
  profile_matches: 'profile_match',
  finished_content: 'finished_content',
};

// ──────────────────────────────────────────────
// Fire-and-forget log writer (never throws)
// Uses the raw (un-patched) .from() to avoid recursion
// ──────────────────────────────────────────────
let _originalFrom: typeof supabase.from | null = null;

function writeLog(
  action: string,
  table: string,
  data?: any,
) {
  // Only log for salesman
  if (_currentUserRole !== 'salesman' || !_currentUserEmail) return;

  // Don't log ignored tables
  if (IGNORED_TABLES.has(table)) return;

  const resourceType = TABLE_RESOURCE_MAP[table] || table;

  // Try to extract a useful resource ID from the data
  let resourceId: string | null = null;
  if (data) {
    const first = Array.isArray(data) ? data[0] : data;
    if (first) {
      resourceId = first.id || first.document_id || first.lead_id || null;
      if (resourceId) resourceId = String(resourceId);
    }
  }

  // Build a human-readable description
  const count = Array.isArray(data) ? data.length : 1;
  const noun = resourceType.replace(/_/g, ' ');
  let description: string;
  switch (action) {
    case 'create':
      description = `Created ${count > 1 ? `${count} ${noun}s` : noun}`;
      break;
    case 'update':
      description = `Updated ${noun}`;
      break;
    case 'upsert':
      description = `Upserted ${count > 1 ? `${count} ${noun}s` : noun}`;
      break;
    case 'delete':
      description = `Deleted ${noun}`;
      break;
    default:
      description = `${action} on ${noun}`;
  }

  // Grab the current page path
  const pagePath = typeof window !== 'undefined' ? window.location.pathname : null;

  // Fire and forget — use raw .from() to avoid recursion
  if (!_originalFrom) return;
  try {
    Promise.resolve(
      _originalFrom('activity_logs')
        .insert({
          user_email: _currentUserEmail,
          user_role: _currentUserRole,
          action,
          resource_type: resourceType,
          resource_id: resourceId,
          description,
          metadata: { table, count } as any,
          page_path: pagePath,
        })
    ).catch(() => {});
  } catch {
    // Silently fail
  }
}

// ──────────────────────────────────────────────
// Monkey-patch installer
// ──────────────────────────────────────────────
let _initialized = false;

export function initSupabaseLogger() {
  if (_initialized) return;
  _initialized = true;

  const originalFrom = supabase.from.bind(supabase);
  _originalFrom = originalFrom;

  // Override .from() to return a proxied query builder
  (supabase as any).from = (table: string) => {
    const builder = originalFrom(table);

    // Skip patching for ignored tables
    if (IGNORED_TABLES.has(table)) return builder;

    // Wrap insert
    const origInsert = builder.insert.bind(builder);
    builder.insert = (values: any, options?: any) => {
      writeLog('create', table, values);
      return origInsert(values, options);
    };

    // Wrap update
    const origUpdate = builder.update.bind(builder);
    builder.update = (values: any, options?: any) => {
      writeLog('update', table, values);
      return origUpdate(values, options);
    };

    // Wrap upsert
    const origUpsert = builder.upsert.bind(builder);
    builder.upsert = (values: any, options?: any) => {
      writeLog('upsert', table, values);
      return origUpsert(values, options);
    };

    // Wrap delete
    const origDelete = builder.delete.bind(builder);
    builder.delete = (options?: any) => {
      writeLog('delete', table);
      return origDelete(options);
    };

    return builder;
  };
}
